import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { styled } from '@mui/material/styles';
import ListItemText from '@mui/material/ListItemText';
import { useMemo } from 'react';
import { ListItemButton, ListItemButtonProps } from '@mui/material';
import FuseNavBadge from '../../FuseNavBadge';
import FuseSvgIcon from '../../../FuseSvgIcon';
import { FuseNavItemComponentProps } from '../../FuseNavItem';
import LucideIcon from '@/components/common/LucideIcon';
import type { LucideIcon as LucideIconType } from 'lucide-react';

type ListItemButtonStyleProps = ListItemButtonProps & {
	itempadding: number;
};

const Root = styled(ListItemButton)<ListItemButtonStyleProps>(({ theme, ...props }) => ({
	height: 50,
	width: '100%',
	borderTopLeftRadius: '8px',
	borderBottomLeftRadius: '8px',
	borderTopRightRadius: '0',
	borderBottomRightRadius: '0',
	margin: '0 0 4px 0',
	paddingRight: 32,
	paddingLeft: props.itempadding > 80 ? 80 : props.itempadding,
	paddingTop: 14,
	paddingBottom: 14,
	marginRight: '-16px',
	marginLeft: '16px',
	color: 'var(--neutral-gray-dark)',
	cursor: 'pointer',
	textDecoration: 'none!important',
	display: 'flex',
	alignItems: 'center',
	transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
	'&:hover': {
		color: 'var(--neutral-gray-dark)',
		backgroundColor: 'var(--neutral-bg-active)',
		'& > .fuse-list-item-icon': { color: 'var(--neutral-gray-dark)' },
		'& > .fuse-list-item-text-primary': { color: 'var(--neutral-gray-dark)' },
	},
	'&:focus': {
		color: 'var(--neutral-gray-dark)',
		backgroundColor: 'var(--neutral-bg-active) !important',
		'& > .fuse-list-item-icon': { color: 'var(--neutral-gray-dark)' },
		'& > .fuse-list-item-text-primary': { color: 'var(--neutral-gray-dark)' },
	},
	'&:active': {
		color: 'var(--neutral-gray-dark)',
		backgroundColor: 'var(--neutral-bg-active) !important',
		'& > .fuse-list-item-icon': { color: 'var(--neutral-gray-dark)' },
		'& > .fuse-list-item-text-primary': { color: 'var(--neutral-gray-dark)' },
	},
	'&.active': {
		color: 'var(--primary-500) !important',
		backgroundColor: 'var(--neutral-bg-active) !important',
		'& > .fuse-list-item-icon': { color: 'var(--primary-500) !important' },
		'& > .fuse-list-item-text-primary': { color: 'var(--primary-500) !important' },
		'& .item-badge': {
			backgroundColor: 'var(--primary-500) !important',
			color: 'var(--neutral-base-white) !important',
		},
	},
	'&.active:hover': {
		color: 'var(--primary-500) !important',
		backgroundColor: 'var(--neutral-bg-active) !important',
		'& > .fuse-list-item-icon': { color: 'var(--primary-500) !important' },
		'& > .fuse-list-item-text-primary': { color: 'var(--primary-500) !important' },
		'& .item-badge': {
			backgroundColor: 'var(--primary-500) !important',
			color: 'var(--neutral-base-white) !important',
		},
	},
	'& > .fuse-list-item-icon': {
		marginRight: 24,
		color: 'var(--neutral-gray-dark)',
		transition: 'color 0.2s ease-in-out',
		fontSize: '16px',
		'& svg': {
			width: '16px !important',
			height: '16px !important',
		},
	},
}));

function FuseNavVerticalItem(props: FuseNavItemComponentProps) {
	const { item, nestedLevel = 0, onItemClick, checkPermission } = props;

	const itempadding = nestedLevel > 0 ? 48 + nestedLevel * 16 : 24;
	const component = item.url ? NavLinkAdapter : 'li';

	const itemProps = useMemo(
		() => ({
			...(component !== 'li' && {
				disabled: item.disabled,
				to: item.url || '',
				end: item.end,
				role: 'button',
				exact: true,
				activeClassName: 'active',
			}),
		}),
		[item, component],
	);

	if (checkPermission && !item?.hasPermission) {
		return null;
	}

	return (
		<Root
			component={component}
			className="fuse-list-item"
			onClick={() => onItemClick && onItemClick(item)}
			itempadding={itempadding}
			sx={{
				'& .MuiTouchRipple-root  .MuiTouchRipple-child': {
					color: 'var(--neutral-gray-medium)',
				},
				'&:active': {
					backgroundColor: 'var(--neutral-bg-active)',
					boxShadow: '0px 2px 1px -1px #3639412B',
				},
			}}
			{...itemProps}
		>
			{item.icon &&
				(typeof item.icon === 'string' ? (
					<FuseSvgIcon
						className="fuse-list-item-icon"
						size={20}
					>
						{item.icon}
					</FuseSvgIcon>
				) : (
					<LucideIcon
						icon={item.icon as LucideIconType}
						className="fuse-list-item-icon"
						size={16}
						color="inherit"
						sx={{
							width: '16px !important',
							height: '16px !important',
							minWidth: '16px !important',
							minHeight: '16px !important',
							fontSize: '16px !important',
							'& svg': {
								width: '16px !important',
								height: '16px !important',
							},
						}}
					/>
				))}

			<ListItemText
				className="fuse-list-item-text"
				primary={item.title}
				secondary={item.subtitle}
				classes={{
					primary:
						nestedLevel === 0
							? 'text-[16px] fuse-list-item-text-primary truncate'
							: 'text-[14px] fuse-list-item-text-primary truncate',
					secondary: 'text-[14px] fuse-list-item-text-secondary truncate',
				}}
				sx={{
					flex: '0 1 auto',
					minWidth: 0,
					'& .MuiListItemText-primary': {
						fontWeight: '500 !important',
						fontSize: nestedLevel === 0 ? '16px !important' : '14px !important',
						color: 'var(--neutral-gray-dark)',
					},
					'& .MuiListItemText-secondary': {
						fontWeight: '500 !important',
						fontSize: '14px !important',
						color: 'var(--neutral-gray-dark)',
					},
					'& .fuse-list-item-text-primary': {
						fontWeight: '500 !important',
						fontSize: nestedLevel === 0 ? '16px !important' : '14px !important',
						color: 'var(--neutral-gray-dark)',
					},
					'& .fuse-list-item-text-secondary': {
						fontWeight: '500 !important',
						fontSize: '14px !important',
						color: 'var(--neutral-gray-dark)',
					},
					'.active &': {
						'& .MuiListItemText-primary': {
							color: 'var(--primary-500) !important',
						},
						'& .fuse-list-item-text-primary': {
							color: 'var(--primary-500) !important',
						},
					},
				}}
			/>
			{item.badge && <FuseNavBadge badge={item.badge} />}
		</Root>
	);
}

export default FuseNavVerticalItem;
